import styled from 'styled-components'
import {
  pxToRem,
  getButtonColor,
  getButtonTextColor,
  getTitleColor,
  getTitleFont,
  getTextFont,
} from 'styles/functions'

interface ContainerProps {
  $bgColor: string
  $textColor: string
}

export const SCPWABannerContainer = styled.div<ContainerProps>`
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 30%;
  height: 30%;
  color: ${({ $textColor }) => $textColor};
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25));
`

export const SCPWABannerMain = styled.div<{ $bgColor: string; $isOpen: boolean; $themeInfos: object }>`
  width: 100%;
  background-color: ${({ $themeInfos, theme, $isOpen }) =>
    $isOpen ? 'transparent' : getButtonColor(theme, $themeInfos, true)};
  border: ${({ $themeInfos, theme, $isOpen }) =>
    $isOpen ? `solid ${getButtonColor(theme, $themeInfos, true)}` : ''};
  border-right-style: none;
  border-left-style: none;
  ${({ theme, $isOpen }) =>
    $isOpen
      ? `clip-path: polygon(
          0% 0%, 100% 0%, 100% 100%,
          calc(50% + ${pxToRem(16)}) 100%,
          calc(50% + ${pxToRem(16)}) calc(100% - ${pxToRem(4)}),
          calc(50% - ${pxToRem(16)}) calc(100% - ${pxToRem(4)}),
          calc(50% - ${pxToRem(16)}) 100%,
          0% 100%
        );`
      : ''}
`

interface SCPWABannerHeaderProps {
  $isOpen: boolean
}

export const SCPWABannerHeader = styled.div<SCPWABannerHeaderProps>`
  padding: ${pxToRem(20)} ${pxToRem(20)} ${pxToRem(20)};
  padding: ${({ $isOpen }) =>
    $isOpen ? `${pxToRem(34)} ${pxToRem(34)} ${pxToRem(34)}` : `${pxToRem(20)} ${pxToRem(20)} ${pxToRem(20)}`};
  text-align: center;
`

interface BannerTitleProps {
  $themeInfos: object
  $isOpen: boolean
}

export const SCPWABannerTitle = styled.h1<BannerTitleProps>`
  display: block;
  font-size: ${pxToRem(22)};
  font-weight: 700;
  font-family: ${({ $themeInfos, theme }) =>
    getTitleFont(theme,$themeInfos)};
  text-align: center;
  color: ${({ $themeInfos, theme, $isOpen }) =>
    $isOpen ? getTitleColor(theme, $themeInfos) : getButtonTextColor(theme)};
`

interface BodyProps {
  $isOpen: boolean
}

export const SCPWABannerBody = styled.div<BodyProps>`
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? pxToRem(500) : '0')};
  transition: max-height 0.35s ease;
`

interface BannerContentProps {
  $themeInfos: object
}

export const SCPWABannerContent = styled.div<BannerContentProps>`
  padding: 0 ${pxToRem(20)} ${pxToRem(34)};
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
  font-family: ${({ $themeInfos, theme }) => getTextFont(theme, $themeInfos)};
  margin-bottom: 1rem;
`

export const SCPWABannerText = styled.p`
  margin: 0;
  font-size: ${pxToRem(14)};
  line-height: 1.5;
  text-align: center;
  opacity: 0.9;
  font-family: inherit;
`

export const SCPWABannerTabRow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
`

interface TabProps {
  $bgColor: string
  $textColor: string
  $isOpen: boolean
  $themeInfos: object
}

export const SCPWABannerTab = styled.button<TabProps>`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: ${pxToRem(38)};
  height: ${pxToRem(19)};
  border-radius: 0 0 ${pxToRem(19)} ${pxToRem(19)};
  background-color: ${({ $themeInfos, theme, $isOpen }) =>
    $isOpen ? 'transparent' : getButtonColor(theme, $themeInfos, true)};
  border: ${({ $themeInfos, theme, $isOpen }) =>
    $isOpen ? `solid ${getButtonColor(theme, $themeInfos, true)}` : 'none'};
  border-top: transparent;
  color: ${({ $textColor }) => $textColor};
  cursor: pointer;
  pointer-events: auto;
  transform: ${({ $isOpen }) => ($isOpen ? 'translate(0, -100%) rotate(180deg)' : '')};
`

interface ChevronProps {
  $isOpen: boolean
  $themeInfos: object
}

export const SCPWABannerChevron = styled.span<ChevronProps>`
  display: inline-block;
  width: ${pxToRem(7)};
  height: ${pxToRem(7)};
  border-right: ${pxToRem(2)} solid currentColor;
  border-bottom: ${pxToRem(2)} solid currentColor;
  transition: transform 0.25s ease;
  transform: rotate(45deg);
  color: ${({ $themeInfos, theme, $isOpen }) =>
    $isOpen ? getButtonColor(theme, $themeInfos, false) : getButtonTextColor(theme)};
`

export const SCPWAInstructions = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${pxToRem(10)};
`

export const SCPWAInstructionsTitle = styled.p`
  margin-top: ${pxToRem(10)};
  font-size: ${pxToRem(14)};
  font-weight: 600;
  font-family: inherit;
`

export const SCPWAInstructionStep = styled.p`
  margin: 0;
  font-size: ${pxToRem(14)};
  line-height: 1.5;
  font-family: inherit;
`

interface InstallButtonProps {
  $themeInfos: object
}

export const SCPWAInstallButton = styled.button<InstallButtonProps>`
  padding: ${pxToRem(10)} ${pxToRem(20)};
  background-color: ${({ $themeInfos, theme }) => getButtonColor(theme, $themeInfos)};
  color: ${({ theme }) => getButtonTextColor(theme)};
  border: none;
  border-radius: ${pxToRem(6)};
  font-size: ${pxToRem(14)};
  font-weight: 600;
  cursor: pointer;
  align-self: center;
  font-family: inherit;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
`

interface LinkProps {
  $textColor: string
}

export const SCPWABannerLink = styled.a<LinkProps>`
  font-size: ${pxToRem(13)};
  color: ${({ $textColor }) => $textColor};
  text-decoration: underline;
  cursor: pointer;
  opacity: 0.6;
  text-align: center;
  font-family: inherit;

  &:hover {
    opacity: 1;
  }
`